import React from 'react';
import { Box, FormControlLabel, TextField, Typography, Paper, RadioGroup, Radio, Switch, Tooltip, Select, MenuItem, FormLabel } from '@mui/material';
import InfoTooltip from '../../InfoTooltip';

interface AdditionalOptionsProps {
  restoration: boolean;
  setRestoration: (v: boolean) => void;
  tdeEnabled: boolean;
  setTdeEnabled: (v: boolean) => void;
  tdeKeystorePath: string;
  setTdeKeystorePath: (v: string) => void;
  showKeystore: boolean;
  startTimeType?: 'now' | 'delay' | 'datetime';
  setStartTimeType?: (v: 'now' | 'delay' | 'datetime') => void;
  startTimeValue?: string;
  setStartTimeValue?: (v: string) => void;
  platform: string;
  sourceTnsAdminEnabled: boolean;
  setSourceTnsAdminEnabled: (v: boolean) => void;
  sourceTnsAdminDir: string;
  setSourceTnsAdminDir: (v: string) => void;
  targetTnsAdminEnabled: boolean;
  setTargetTnsAdminEnabled: (v: boolean) => void;
  targetTnsAdminDir: string;
  setTargetTnsAdminDir: (v: string) => void;
  dropGrpAfterPatching: boolean;
  setDropGrpAfterPatching: (v: boolean) => void;
  delAfterUpgradePfileEnabled: boolean;
  setDelAfterUpgradePfileEnabled: (v: boolean) => void;
  delAfterUpgradePfile: string;
  setDelAfterUpgradePfile: (v: string) => void;
  addDuringUpgradePfileEnabled: boolean;
  setAddDuringUpgradePfileEnabled: (v: boolean) => void;
  addDuringUpgradePfile: string;
  setAddDuringUpgradePfile: (v: string) => void;
  // binaryOptionRac and clusterNodes kept for interface compatibility but no longer rendered here
  // (moved to Home Settings tab)
  binaryOptionRac: boolean | undefined;
  setBinaryOptionRac: (v: boolean | undefined) => void;
  clusterNodesEnabled: boolean;
  setClusterNodesEnabled: (v: boolean) => void;
  clusterNodes: string;
  setClusterNodes: (v: string) => void;
  showOnlyRac?: boolean;
  executionMode?: string;
  racRolling?: 'DISABLED' | 'REQUIRED' | 'FORCE' | 'AUTO';
  setRacRolling?: (v: 'DISABLED' | 'REQUIRED' | 'FORCE' | 'AUTO') => void;
  drainTimeout?: string;
  setDrainTimeout?: (v: string) => void;
}

const AdditionalOptions: React.FC<AdditionalOptionsProps> = ({
  restoration,
  setRestoration,
  tdeEnabled,
  setTdeEnabled,
  tdeKeystorePath,
  setTdeKeystorePath,
  showKeystore,
  startTimeType,
  setStartTimeType,
  startTimeValue,
  setStartTimeValue,
  platform,
  sourceTnsAdminEnabled,
  setSourceTnsAdminEnabled,
  sourceTnsAdminDir,
  setSourceTnsAdminDir,
  targetTnsAdminEnabled,
  setTargetTnsAdminEnabled,
  targetTnsAdminDir,
  setTargetTnsAdminDir,
  dropGrpAfterPatching,
  setDropGrpAfterPatching,
  delAfterUpgradePfileEnabled,
  setDelAfterUpgradePfileEnabled,
  delAfterUpgradePfile,
  setDelAfterUpgradePfile,
  addDuringUpgradePfileEnabled,
  setAddDuringUpgradePfileEnabled,
  addDuringUpgradePfile,
  setAddDuringUpgradePfile,
  binaryOptionRac,
  setBinaryOptionRac,
  clusterNodesEnabled,
  setClusterNodesEnabled,
  clusterNodes,
  setClusterNodes,
  showOnlyRac = false,
  executionMode,
  racRolling,
  setRacRolling,
  drainTimeout,
  setDrainTimeout,
}) => {
  const [dateWarning, setDateWarning] = React.useState('');
  const [drainTimeoutMode, setDrainTimeoutMode] = React.useState<'seconds' | 'wait'>(
    drainTimeout === 'WAIT' ? 'wait' : 'seconds'
  );

  React.useEffect(() => {
    if (drainTimeout === 'WAIT') {
      setDrainTimeoutMode('wait');
    } else if (drainTimeout && drainTimeout !== '') {
      setDrainTimeoutMode('seconds');
    }
  }, [drainTimeout]);
  const [tdeKeystorePathTouched, setTdeKeystorePathTouched] = React.useState(false);

  React.useEffect(() => {
    if (startTimeType === 'datetime' && startTimeValue && startTimeValue.length === 14) {
      const d = startTimeValue;
      const inputDate = new Date(
        Number(d.slice(4, 8)), // year
        Number(d.slice(2, 4)) - 1, // month (0-based)
        Number(d.slice(0, 2)), // day
        Number(d.slice(8, 10)), // hour
        Number(d.slice(10, 12)), // min
        Number(d.slice(12, 14)) // sec
      );
      if (inputDate.getTime() < Date.now()) {
        setDateWarning('Warning: Date cannot be in the past.');
      } else {
        setDateWarning('');
      }
    } else {
      setDateWarning('');
    }
  }, [startTimeType, startTimeValue]);

  React.useEffect(() => {
    setTdeKeystorePathTouched(false);
  }, [platform]);

  function formatDateTimeInput(raw: string) {
    // Only digits, up to 14: ddmmyyyyhhmmss
    const v = raw.replace(/\D/g, '').slice(0, 14);
    let out = '';
    if (v.length > 0) out += v.slice(0, 2);
    if (v.length > 2) out += '/' + v.slice(2, 4);
    if (v.length > 4) out += '/' + v.slice(4, 8);
    if (v.length > 8) out += ' ' + v.slice(8, 10);
    if (v.length > 10) out += ':' + v.slice(10, 12);
    if (v.length > 12) out += ':' + v.slice(12, 14);
    return out;
  }

  return (
    <Paper elevation={0} sx={{ p: 3, background: 'background.paper', borderRadius: 3 }}>
      {!showOnlyRac && (
        <>
          {/* First row: RAC (left) + GRP (right) side by side */}
          <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 3, mb: 3, alignItems: 'flex-start' }}>
            {/* GRP Section */}
            <Paper elevation={0} sx={{ flex: '0 0 35%', order: 2, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>GRP</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={restoration} onChange={e => setRestoration(e.target.checked)} color="primary" />}
                  label={
                    <Tooltip
                      title="Generates a Guaranteed Restore Point (GRP) for database restoration."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Restoration</Typography>
                    </Tooltip>
                  }
                />
                <FormControlLabel
                  control={<Switch checked={dropGrpAfterPatching} onChange={e => setDropGrpAfterPatching(e.target.checked)} color="primary" disabled={!restoration} />}
                  label={
                    <Tooltip
                      title="Deletes the Guaranteed Restore Point (GRP) after database patching."
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                            color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                            fontSize: 15,
                            borderRadius: 2,
                            boxShadow: 3,
                            px: 2,
                            py: 1,
                            fontWeight: 400,
                            minWidth: 320,
                          },
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Drop GRP After Patching</Typography>
                    </Tooltip>
                  }
                />
              </Box>
            </Paper>

          {/* Keystore Section - Full Width */}
          {showKeystore && (
            <Box sx={{ mb: 3 }}>
              <Paper elevation={0} sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>Keystore</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={tdeKeystorePathTouched ? tdeKeystorePath : ''}
                  onChange={e => { setTdeKeystorePathTouched(true); setTdeKeystorePath(e.target.value); }}
                  onFocus={() => { if (!tdeKeystorePathTouched && tdeKeystorePath === '') setTdeKeystorePath(''); setTdeKeystorePathTouched(true); }}
                  placeholder={platform === 'WINDOWS.X64' ? 'C:\\Oracle\\AutoUpgrade\\Keystore' : '/home/oracle/autoupgrade/keystore'}
                  InputProps={{
                    sx: {
                      '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                      '& .MuiInputBase-input': {
                        color: !tdeKeystorePathTouched && tdeKeystorePath === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      fontSize: 15,
                      background: 'transparent',
                    },
                  }}
                />
              </Paper>
            </Box>
          )}
            {/* RAC Section - only RAC Rolling + Drain Timeout, only for analyze/deploy */}
            {(executionMode === 'deploy' || executionMode === 'analyze') && (
              <Paper elevation={0} sx={{ flex: 1, minWidth: 0, order: 1, p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main }}>RAC</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ mt: 0 }}>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>
                        <Box sx={{ pt: 1 }}>
                          <Typography sx={{ mb: 1 }}>&nbsp;</Typography>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>DISABLED:</Typography>
                          <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                            RAC Rolling will not be used. This forces the previous behavior where all RAC nodes are stopped and patched, leading to database downtime during the patching window. Note: This will be the default for version 26.1 and is changed to AUTO on 26.2 onwards.
                          </Typography>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>REQUIRED:</Typography>
                          <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                            RAC Rolling must be used. The patching operation will only proceed if RAC Rolling is possible. If conditions prevent RAC Rolling, AutoUpgrade will stop before making any changes. Conditions that would stop AutoUpgrade include:
                          </Typography>
                          <ul style={{ marginTop: 0, marginBottom: 12, paddingLeft: 20, fontSize: 15 }}>
                            <li>The database is not running on a RAC</li>
                            <li>There is a patch deployed on the target Oracle Home that is not RAC Rolling</li>
                          </ul>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>FORCE:</Typography>
                          <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                            Similar to REQUIRED, but skips the checks that would otherwise prevent RAC Rolling from proceeding. This forces the use of the RAC Rolling method regardless of certain conditions. Use case: Useful if you have applied a non-RAC Rolling Data Pump Bundle Patch but have no Data Pump jobs running. Use with caution.
                          </Typography>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>AUTO (default):</Typography>
                          <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                            Automatically decides the best approach, resolving to either DISABLED or REQUIRED. If all preconditions are met, AUTO resolves as REQUIRED. Otherwise, AUTO resolves as DISABLED. Preconditions for AUTO to resolve as REQUIRED:
                          </Typography>
                          <ul style={{ marginTop: 0, marginBottom: 12, paddingLeft: 20, fontSize: 15 }}>
                            <li>The database is in RAC mode</li>
                            <li>All patches applied to the new home are RAC Rolling</li>
                          </ul>
                          <Typography sx={{ mt: 1 }}>&nbsp;</Typography>
                        </Box>
                      </Typography>
                    }
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          borderRadius: 8,
                          fontSize: 16,
                          py: 2.2,
                          px: 4,
                          boxShadow: 3,
                          maxWidth: 1100,
                          minWidth: 220,
                        },
                      },
                      arrow: {
                        sx: {
                          color: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, display: 'inline', cursor: 'help' }}>RAC Rolling:</Typography>
                  </Tooltip>
                </FormLabel>
                <Select
                  size="small"
                  value={racRolling || 'AUTO'}
                  onChange={e => setRacRolling && setRacRolling(e.target.value as 'DISABLED' | 'REQUIRED' | 'FORCE' | 'AUTO')}
                  sx={{ width: 150 }}
                >
                  <MenuItem value="DISABLED">Disabled</MenuItem>
                  <MenuItem value="REQUIRED">Required</MenuItem>
                  <MenuItem value="FORCE">Force</MenuItem>
                  <MenuItem value="AUTO">Auto</MenuItem>
                </Select>
              </Box>
              <Box sx={{ mt: 2 }}>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>
                        <Box sx={{ pt: 1 }}>
                          <Typography sx={{ mb: 1 }}>&nbsp;</Typography>
                          <Typography sx={{ mb: 1.5, fontSize: 15 }}>
                            This parameter defines how long AutoUpgrade should wait, in seconds, until the sessions are drained before stopping an instance during RAC Rolling operations.
                          </Typography>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>Time in seconds (integer greater than or equal to 0):</Typography>
                          <Typography sx={{ mb: 1, fontSize: 15 }}>
                            When a positive integer value is provided:
                          </Typography>
                          <ul style={{ marginTop: 0, marginBottom: 12, paddingLeft: 20, fontSize: 15 }}>
                            <li>The value is passed to the srvctl stop instance command with -drain_timeout option</li>
                            <li>Services are stopped immediately and moved to another node (failover)</li>
                            <li>Sessions are prompted to reconnect and continue their workload on a different instance</li>
                            <li>Sessions without an open transaction are not allowed to continue</li>
                            <li>Sessions with uncommitted work will have the specified number of seconds before they are killed</li>
                            <li>If session draining completes before the timeout value is reached, AutoUpgrade proceeds immediately</li>
                            <li>Sessions configured with AC/TAC (Application Continuity/Transparent Application Continuity) can continue their uncommitted work on the surviving instance</li>
                          </ul>
                          <Typography sx={{ mb: 1, fontSize: 15 }}>
                            When the value is 0 (ZERO): Sessions are immediately killed after the service is relocated.
                          </Typography>
                          <Typography sx={{ mb: 1, fontSize: 15 }}>
                            When not provided (NULL): AutoUpgrade will use the maximum drain timeout of all currently up and running services on the instance that is being stopped.
                          </Typography>
                          <Typography sx={{ fontWeight: 600, mb: 1 }}>WAIT:</Typography>
                          <Typography sx={{ mb: 1, fontSize: 15 }}>
                            When WAIT is provided, the RAC Rolling process gives full control to the end-user over when AutoUpgrade will proceed and stop each instance:
                          </Typography>
                          <ul style={{ marginTop: 0, marginBottom: 12, paddingLeft: 20, fontSize: 15 }}>
                            <li>Services running on an instance are first relocated to another node, ensuring new sessions connect to the new node</li>
                            <li>The system then waits until the user types the "proceed" command on the AutoUpgrade command-line interface</li>
                            <li>Once the "proceed" command is given, the process continues with the stop command for the instance</li>
                            <li>Uses stopoption TRANSACTIONAL, which relocates the service to a surviving node and waits for sessions to finish their transactions (indefinitely, up to 30 days)</li>
                          </ul>
                          <Typography sx={{ mt: 1 }}>&nbsp;</Typography>
                        </Box>
                      </Typography>
                    }
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          borderRadius: 8,
                          fontSize: 16,
                          py: 2.2,
                          px: 4,
                          boxShadow: 3,
                          maxWidth: 1100,
                          minWidth: 220,
                        },
                      },
                      arrow: {
                        sx: {
                          color: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, display: 'inline', cursor: 'help' }}>Drain Timeout:</Typography>
                  </Tooltip>
                </FormLabel>
                <RadioGroup
                  row
                  value={drainTimeoutMode}
                  onChange={e => {
                    const mode = e.target.value as 'seconds' | 'wait';
                    setDrainTimeoutMode(mode);
                    if (mode === 'wait') {
                      setDrainTimeout && setDrainTimeout('WAIT');
                    } else {
                      setDrainTimeout && setDrainTimeout(drainTimeout && drainTimeout !== 'WAIT' ? drainTimeout : '');
                    }
                  }}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel value="seconds" control={<Radio size="small" />} label="Time in seconds" />
                  <FormControlLabel value="wait" control={<Radio size="small" />} label="Wait" />
                </RadioGroup>
                {drainTimeoutMode === 'seconds' && (
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={drainTimeout && drainTimeout !== 'WAIT' ? drainTimeout : ''}
                    onChange={e => {
                      const value = e.target.value;
                      // Prevent negative values
                      if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
                        setDrainTimeout && setDrainTimeout(value);
                      }
                    }}
                    placeholder="e.g., 300"
                    sx={{ width: 150, mt: 1 }}
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& input[type=number]': {
                          color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                          '&::-webkit-inner-spin-button': {
                            opacity: 1,
                            WebkitAppearance: 'auto',
                            filter: theme => theme.palette.mode === 'dark' ? 'invert(1) brightness(2)' : 'none',
                            height: '20px',
                          },
                          '&::-webkit-outer-spin-button': {
                            opacity: 1,
                            WebkitAppearance: 'auto',
                            filter: theme => theme.palette.mode === 'dark' ? 'invert(1) brightness(2)' : 'none',
                            height: '20px',
                          },
                        },
                        fontSize: 15,
                        background: 'transparent',
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Paper>
            )}
          </Box>
        </>
      )}

      {!showOnlyRac && (
        <>
          {/* Divider */}
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 3 }} />

          {/* Start Time Section - Full Width */}
          <Box>
            <Paper elevation={0} sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2, background: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: theme => theme.palette.primary.main, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                Start Time
                <InfoTooltip
                  title="Sets a future start time for the upgrade job to run. Use this parameter to schedule upgrade jobs to balance the load on your server, and to prevent multiple jobs from starting immediately."
                  placement="right"
                />
              </Typography>
              <RadioGroup
                name="start-time-type-additional"
                value={startTimeType}
                onChange={e => (setStartTimeType ? setStartTimeType(e.target.value as 'now' | 'delay' | 'datetime') : undefined)}
              >
                <FormControlLabel value="now" control={<Radio />} label="Now" />
                <FormControlLabel value="delay" control={<Radio />} label={'+XhYm (delay after launch)'} />
                <FormControlLabel value="datetime" control={<Radio />} label="dd/mm/yyyy hh:mm:ss" />
              </RadioGroup>
              {startTimeType === 'delay' && (
                <TextField
                  size="small"
                  label="Delay (e.g. +2h30m)"
                  value={startTimeValue}
                  onChange={e => (setStartTimeValue ? setStartTimeValue(e.target.value) : undefined)}
                  placeholder="+1h0m"
                  sx={{ mt: 1, width: '100%' }}
                />
              )}
              {startTimeType === 'datetime' && (
                <TextField
                  size="small"
                  label="Date & Time (dd/mm/yyyy hh:mm:ss)"
                  value={formatDateTimeInput(startTimeValue || '')}
                  onChange={e => {
                    const raw = e.target.value.replace(/\D/g, '').slice(0, 14);
                    if (setStartTimeValue) setStartTimeValue(raw);
                  }}
                  placeholder="10/05/2025 14:30:00"
                  sx={{ mt: 1, width: '100%' }}
                  error={!!dateWarning}
                  helperText={dateWarning}
                />
              )}
            </Paper>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default AdditionalOptions;
